import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';

// ===== ANALYTICS TAB COMPONENT =====
const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('at_risk');

  useEffect(() => {
    axios.get(`${API}/admin/analytics`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center items-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
      <span className="ml-3 text-gray-500">Analytics werden geladen...</span>
    </div>
  );
  if (!data) return <p className="text-red-500 p-4">Fehler beim Laden der Analytics.</p>;

  const { summary, exam_stats, user_stats, category_stats } = data;

  const examTotal = Object.values(exam_stats).reduce((a, b) => a + b, 0) || 1;

  const filtered = (user_stats || []).filter(u => {
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const matchSearch = !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    return matchRole && matchSearch;
  }).sort((a, b) => {
    if (sortBy === 'at_risk') return (b.at_risk ? 1 : 0) - (a.at_risk ? 1 : 0) || a.completion_pct - b.completion_pct;
    if (sortBy === 'completion_asc') return a.completion_pct - b.completion_pct;
    if (sortBy === 'completion_desc') return b.completion_pct - a.completion_pct;
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return 0;
  });

  const examStatusLabel = {
    exam_passed: { label: 'Bestanden', bg: 'bg-green-100', text: 'text-green-800' },
    exam_failed: { label: 'Nicht bestanden', bg: 'bg-red-100', text: 'text-red-800' },
    exam_booked: { label: 'Termin gebucht', bg: 'bg-blue-100', text: 'text-blue-800' },
    ready_for_exam: { label: 'Bereit', bg: 'bg-indigo-100', text: 'text-indigo-800' },
    null: { label: 'Noch nicht bereit', bg: 'bg-gray-100', text: 'text-gray-500' },
  };
  const getExamBadge = (s) => examStatusLabel[s] || examStatusLabel[null];

  return (
    <div className="space-y-8" data-testid="analytics-tab">

      {/* ── SUMMARY CARDS ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Lernende gesamt', value: summary.total_learners, icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Ø Abschluss', value: `${summary.avg_completion_pct}%`, icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', color: 'text-teal-600', bg: 'bg-teal-50' },
          { label: 'Bestanden', value: exam_stats.passed, icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Nachholbedarf', value: summary.at_risk_count, icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', color: summary.at_risk_count > 0 ? 'text-red-600' : 'text-gray-400', bg: summary.at_risk_count > 0 ? 'bg-red-50' : 'bg-gray-50' },
        ].map((card, i) => (
          <div key={i} className={`stat-card card-enter ${card.bg} rounded-xl p-5 border border-white shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">{card.label}</span>
              <svg className={`w-5 h-5 ${card.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
              </svg>
            </div>
            <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* ── EXAM STATS ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Abschlussgespräch – Übersicht</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Bestanden', count: exam_stats.passed, color: 'bg-green-500' },
            { label: 'Nicht bestanden', count: exam_stats.failed, color: 'bg-red-500' },
            { label: 'Termin gebucht', count: exam_stats.booked, color: 'bg-blue-500' },
            { label: 'Bereit', count: exam_stats.ready, color: 'bg-indigo-500' },
            { label: 'Noch nicht', count: exam_stats.not_started, color: 'bg-gray-300' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl font-bold text-gray-900">{s.count}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full ${s.color} rounded-full transition-all`} style={{ width: `${(s.count / examTotal) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CATEGORY COMPLETION ── */}
      {category_stats && category_stats.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Kategorie-Abschlussquoten</h3>
          <div className="space-y-3">
            {category_stats.map(cat => (
              <div key={cat.category_id} className="flex items-center gap-3">
                <div className="w-44 text-sm text-gray-700 truncate shrink-0">{cat.title}</div>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${cat.completion_pct >= 80 ? 'bg-green-500' : cat.completion_pct >= 40 ? 'bg-yellow-400' : 'bg-red-400'}`}
                    style={{ width: `${cat.completion_pct}%` }}
                  />
                </div>
                <div className="w-20 text-right text-sm font-medium text-gray-700 shrink-0">
                  {cat.completed_users}/{cat.total_learners} ({cat.completion_pct}%)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── USER PROGRESS TABLE ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h3 className="text-base font-semibold text-gray-900">Lernfortschritt je Benutzer</h3>
          <div className="flex flex-wrap gap-2">
            {/* Search */}
            <input
              type="text"
              placeholder="Name / E-Mail suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 w-52"
              data-testid="analytics-search"
            />
            {/* Role filter */}
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              data-testid="analytics-role-filter"
            >
              <option value="all">Alle Rollen</option>
              <option value="vertriebler">Vertriebler</option>
              <option value="teamleiter">Teamleiter</option>
            </select>
            {/* Sort */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              data-testid="analytics-sort"
            >
              <option value="at_risk">Nachholbedarf zuerst</option>
              <option value="completion_asc">Fortschritt aufsteigend</option>
              <option value="completion_desc">Fortschritt absteigend</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-gray-400 text-sm py-8 text-center">Keine Einträge gefunden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="pb-3 pr-4">Name</th>
                  <th className="pb-3 pr-4">Rolle</th>
                  <th className="pb-3 pr-4 w-48">Fortschritt</th>
                  <th className="pb-3 pr-4">Abschlussgespräch</th>
                  <th className="pb-3 pr-4">Zuletzt aktiv</th>
                  <th className="pb-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(u => {
                  const examBadge = getExamBadge(u.exam_status);
                  const barColor = u.completion_pct >= 80 ? 'bg-green-500' : u.completion_pct >= 40 ? 'bg-yellow-400' : 'bg-red-400';
                  return (
                    <tr key={u.user_id} className={`${u.at_risk ? 'bg-red-50' : 'hover:bg-gray-50'} transition-colors`} data-testid={`analytics-row-${u.user_id}`}>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{u.name}</div>
                        <div className="text-xs text-gray-400">{u.email}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="capitalize text-xs font-medium text-gray-600">{u.role}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-[80px]">
                            <div className={`h-full ${barColor} rounded-full`} style={{ width: `${u.completion_pct}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-gray-700 w-10 text-right">{u.completion_pct}%</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{u.completed_modules}/{u.total_modules} Module</div>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${examBadge.bg} ${examBadge.text}`}>
                          {examBadge.label}
                        </span>
                        {u.failure_count > 0 && (
                          <div className="text-xs text-red-500 mt-1">{u.failure_count}× nicht bestanden</div>
                        )}
                        {u.cancellation_count > 0 && (
                          <div className="text-xs text-orange-500">{u.cancellation_count}× abgesagt</div>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {u.last_active ? (
                          <>
                            <div className="text-xs text-gray-600">
                              {new Date(u.last_active).toLocaleDateString('de-DE')}
                            </div>
                            {u.days_inactive !== null && (
                              <div className={`text-xs ${u.days_inactive >= 7 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                                vor {u.days_inactive} Tag{u.days_inactive !== 1 ? 'en' : ''}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-300">Noch keine Aktivität</span>
                        )}
                      </td>
                      <td className="py-3">
                        {u.at_risk ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Nachholbedarf
                          </span>
                        ) : u.completion_pct >= 100 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Abgeschlossen
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">In Bearbeitung</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsTab;
